import { formatPrice } from '../format'
import type { VolumePricingCurve } from '../types'

export interface CatalogPriceBreaksProps {
  readonly curve: VolumePricingCurve
  readonly currency: string
}

/**
 * `Fictiv Platform.dc.html:1737-1739` — STATIC quantity-RANGE break cards
 * for a CATALOG (`misumi_standard`) part's pricing card ("Set catalog
 * price · volume breaks" mode, `isBreaks`, no curve chart on this page).
 *
 * This is deliberately a SEPARATE component from `VolumeCurveVisual`'s
 * `VolumeBreaksList` rather than a new mode bolted onto it:
 * `VolumeBreaksList` is an interactive per-tier list (`<button>`,
 * `aria-pressed`, `onFocus`/`onMouseEnter`/`onClick`) that syncs an
 * `activeMinQuantity` with the curve chart it's always paired with
 * (`VolumePricingPopoverShell`'s Qty-cell popover, `VolumeCurveChart` +
 * `VolumeBreaksList` together). A catalog part-detail page has no chart to
 * sync with, so that interactivity would be meaningless here — worse, a
 * focusable/clickable element that does nothing observable is an
 * accessibility smell (a control with no discernible effect). These cards
 * are plain, non-interactive `<div>`s instead.
 *
 * The other visible difference from `VolumeBreaksList`: each card's label
 * is a quantity RANGE ("Qty 1-9", "Qty 10-49", …, "Qty 200+" for the last
 * tier) computed from consecutive tier boundaries, not a single quantity
 * ("Qty 1") — the prototype's break cards describe the qty BAND a price
 * applies to, not just its starting point.
 */
export function CatalogPriceBreaks({ curve, currency }: CatalogPriceBreaksProps) {
  const tiers = [...curve.tiers].sort((a, b) => a.minQuantity - b.minQuantity)
  if (tiers.length === 0) return null

  return (
    <div className="catalog-price-breaks">
      {tiers.map((tier, i) => {
        const next = tiers[i + 1]
        const label = next
          ? `Qty ${tier.minQuantity}-${next.minQuantity - 1}`
          : `Qty ${tier.minQuantity}+`
        return (
          <div key={tier.minQuantity} className="catalog-price-break-card">
            <div className="catalog-price-break-qty">{label}</div>
            <div className="catalog-price-break-price">
              {formatPrice({ amount: tier.unitPriceAmount, currency })}
            </div>
            <div className="catalog-price-break-suffix">per unit</div>
          </div>
        )
      })}
    </div>
  )
}
